// TYPES
import { useSelector } from 'react-redux';
import type { TAttributeValueArrayPair } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import { Button, Col, Row, Select, Space } from '@timeedit/ui-components/antd';
import useSelectSearch from '../../../../hooks/useSelectSearch';
// REDUX
import { organizationNodesSelector } from '../../../../slices/organizationHierarchy.slice';
import { rolesSelector } from '../../../../slices/roles.slice';

type Props = {
  orgAndRolePairs: TAttributeValueArrayPair[];
  onChange: (newVal: TAttributeValueArrayPair[]) => void;
  hasError?: boolean;
};

type RowProps = {
  orgAndRole: TAttributeValueArrayPair;
  onChange: (newVal: TAttributeValueArrayPair) => void;
  onDeleteRow: () => void;
  allowDelete: boolean;
  hasError?: boolean;
};

const OrgAndRollCellRow = ({
  orgAndRole,
  onChange,
  onDeleteRow,
  allowDelete,
  hasError,
}: RowProps) => {
  const allRoles = useSelector(rolesSelector);
  const allOrganizationNodes = useSelector(organizationNodesSelector);

  const [roles, setRoleFilter] = useSelectSearch(allRoles, 'name', 'name');
  const [nodes, setNodeFilter] = useSelectSearch(
    allOrganizationNodes,
    'id',
    'name'
  );

  const onUpdateCell = (newVal: string, cellIdx: number) => {
    const updatedRow: TAttributeValueArrayPair = [
      cellIdx === 0 ? newVal : orgAndRole[0],
      cellIdx === 1 ? newVal : orgAndRole[1],
    ];
    onChange(updatedRow);
  };

  return (
    <Space direction="horizontal" style={{ width: '100%' }}>
      <Select
        style={{ width: '100px' }}
        size="small"
        value={
          orgAndRole[0] && orgAndRole[0] !== '' ? orgAndRole[0] : undefined
        }
        onChange={(newVal) => onUpdateCell(newVal, 0)}
        placeholder="Node"
        showSearch
        onSearch={setNodeFilter}
        filterOption={false}
        options={nodes}
        popupMatchSelectWidth={false}
        status={hasError && !orgAndRole[0] ? 'error' : undefined}
      />
      <Select
        style={{ width: '100px' }}
        size="small"
        value={
          orgAndRole[1] && orgAndRole[1] !== '' ? orgAndRole[1] : undefined
        }
        onChange={(newVal) => onUpdateCell(newVal, 1)}
        placeholder="Role"
        showSearch
        onSearch={setRoleFilter}
        filterOption={false}
        options={roles}
        status={hasError && !orgAndRole[1] ? 'error' : undefined}
      />
      <Button
        type="link"
        danger
        ghost
        onClick={onDeleteRow}
        disabled={!allowDelete}
        icon={<Icon id="DeleteOutlined" alt="Delete" />}
        style={{ width: '16px' }}
      />
    </Space>
  );
};

const OrgAndRoleCell = ({ orgAndRolePairs, onChange, hasError }: Props) => {
  const onUpdateRow = (newValue: TAttributeValueArrayPair, rowIdx: number) => {
    const updatedVal = [
      ...orgAndRolePairs.slice(0, rowIdx),
      newValue,
      ...orgAndRolePairs.slice(rowIdx + 1),
    ];
    onChange(updatedVal);
  };

  const onAddRow = () => {
    onChange([...orgAndRolePairs, ['', '']]);
  };

  const onDeleteRow = (rowIdx: number) => {
    onChange([
      ...orgAndRolePairs.slice(0, rowIdx),
      ...orgAndRolePairs.slice(rowIdx + 1),
    ]);
  };

  return (
    <>
      <Row>
        <Col style={{ width: '100%' }}>
          {orgAndRolePairs.map((pair, rowIdx) => (
            <OrgAndRollCellRow
              key={`org-role-${rowIdx}`}
              orgAndRole={pair}
              onChange={(newVal) => onUpdateRow(newVal, rowIdx)}
              onDeleteRow={() => onDeleteRow(rowIdx)}
              allowDelete={
                !(!orgAndRolePairs.length || orgAndRolePairs.length <= 1)
              }
              hasError={hasError}
            />
          ))}
        </Col>
      </Row>
      <Row justify="end">
        <Button type="link" onClick={onAddRow}>
          + Add pair
        </Button>
      </Row>
    </>
  );
};

export default OrgAndRoleCell;
